// =============================================================
// קובץ: src/services/backoffice/couponsService.ts
// שירות ניהול קופוני גישה חופשית (Backoffice)
// =============================================================

import { handleSessionError } from './authService';
import { API_BASE_URL } from '../../config/api';

// ============== Interfaces ==============

export interface Coupon {
    id: number;
    code: string;
    campaign_name: string;
    duration_hours: number;
    window_start: string;
    window_end: string;
    is_active: number;
    created_at: string;
    currently_active?: number;   // 1 אם is_active=1 וגם window_end > NOW()
    total_used?: number;         // כמה השתמשו בקופון
    total_converted?: number;    // חדשים שהמירו לתשלום
}

export interface CouponStats {
    coupon: Coupon;
    total_used: number;   // כמה משתמשים השתמשו בקופון
    new_users: number;    // מתוכם הגיעו חדשים דרך הקופון
    converted: number;    // מתוך החדשים - כמה רכשו מנוי אח"כ
}

// ============== Helper Functions ==============

const createFormData = (data: Record<string, any>): FormData => {
    const formData = new FormData();

    const sessionToken = localStorage.getItem('backoffice_session_id');
    if (sessionToken) {
        formData.append('session_token', sessionToken);
    }

    Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            formData.append(key, value.toString());
        }
    });
    return formData;
};

const handleApiResponse = async (response: Response): Promise<any> => {
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (handleSessionError(result)) {
        throw new Error('Session expired - redirecting to login');
    }

    return result;
};

// ============== Service Class ==============

class CouponsService {
    /** רשימת כל הקופונים + ספירת שימושים/המרות לכל מבצע */
    async getCoupons(): Promise<Coupon[]> {
        try {
            const formData = createFormData({});
            const response = await fetch(
                `${API_BASE_URL}/Api/service/backoffice/get_coupons`,
                { method: 'POST', body: formData }
            );
            const result = await handleApiResponse(response);
            if (result.res === 'NOK') {
                throw new Error(result.msg || 'Failed to fetch coupons');
            }
            return result.data?.coupons || result.coupons || [];
        } catch (error) {
            console.error('Coupons getCoupons error:', error);
            throw error;
        }
    }

    /** יצירת קופון חדש (הפעלה מיידית - החלון מחושב בשרת מ-duration_hours) */
    async createCoupon(data: {
        code: string;
        campaign_name: string;
        duration_hours: string | number;
    }): Promise<Coupon> {
        try {
            const formData = createFormData(data);
            const response = await fetch(
                `${API_BASE_URL}/Api/service/backoffice/create_coupon`,
                { method: 'POST', body: formData }
            );
            const result = await handleApiResponse(response);
            if (result.res === 'NOK') {
                throw new Error(result.msg || 'Failed to create coupon');
            }
            return result.data;
        } catch (error) {
            console.error('Coupons createCoupon error:', error);
            throw error;
        }
    }

    /** הפעלה/כיבוי ידני של קופון */
    async toggleCoupon(coupon_id: number, is_active: boolean): Promise<{ coupon_id: number; is_active: number }> {
        try {
            const formData = createFormData({
                coupon_id,
                is_active: is_active ? '1' : '0',
            });
            const response = await fetch(
                `${API_BASE_URL}/Api/service/backoffice/toggle_coupon`,
                { method: 'POST', body: formData }
            );
            const result = await handleApiResponse(response);
            if (result.res === 'NOK') {
                throw new Error(result.msg || 'Failed to toggle coupon');
            }
            return result.data;
        } catch (error) {
            console.error('Coupons toggleCoupon error:', error);
            throw error;
        }
    }

    /** סטטיסטיקת מבצע בודד */
    async getCouponStats(coupon_id: number): Promise<CouponStats> {
        try {
            const formData = createFormData({ coupon_id });
            const response = await fetch(
                `${API_BASE_URL}/Api/service/backoffice/get_coupon_stats`,
                { method: 'POST', body: formData }
            );
            const result = await handleApiResponse(response);
            if (result.res === 'NOK') {
                throw new Error(result.msg || 'Failed to fetch coupon stats');
            }
            return result.data;
        } catch (error) {
            console.error('Coupons getCouponStats error:', error);
            throw error;
        }
    }
}

export const couponsService = new CouponsService();
